import type { ThemeConfig } from "antd";

/**
 * Design tokens. One brand accent (the school's green) used for primary actions and state; everything else is a calm
 * neutral. Hairline borders instead of shadows; shadows only on overlays. 4/8 px spacing rhythm.
 */
export const brand = {
  green: "#173f35",
  greenHover: "#1f5245",
  greenDeep: "#0f2b24",
  cream: "#f6f5f1",
  ink: "#1c2420",
  muted: "#5f6b66",
  border: "#e6e4dd",
  borderStrong: "#d9d6cc",
} as const;

export const theme: ThemeConfig = {
  token: {
    colorPrimary: brand.green,
    colorLink: brand.green,
    colorLinkHover: brand.greenHover,
    colorText: brand.ink,
    colorTextSecondary: brand.muted,
    colorBorder: brand.borderStrong,
    colorBorderSecondary: brand.border,
    colorBgLayout: brand.cream,
    colorSuccess: "#2f7d4f",
    colorWarning: "#b7791f",
    colorError: "#c2412d",
    colorInfo: "#2b6cb0",
    fontFamily: "Inter, 'SF Pro Text', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    fontSize: 14,
    lineHeight: 1.5,
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    controlHeight: 36,
    controlHeightSM: 28,
    controlHeightLG: 40,
    boxShadow: "0 4px 12px -2px rgba(17, 24, 20, 0.08)",
    boxShadowSecondary: "0 12px 32px -8px rgba(17, 24, 20, 0.18)",
    motionDurationMid: "0.15s",
    motionDurationSlow: "0.18s",
  },
  components: {
    Layout: {
      siderBg: brand.green,
      triggerBg: brand.greenDeep,
      headerBg: "#ffffff",
      headerHeight: 56,
      headerPadding: "0 24px",
      bodyBg: brand.cream,
    },
    Menu: {
      darkItemBg: brand.green,
      darkSubMenuItemBg: brand.green,
      darkItemColor: "rgba(255, 255, 255, 0.72)",
      darkItemHoverColor: "#ffffff",
      darkItemHoverBg: "rgba(255, 255, 255, 0.06)",
      darkItemSelectedBg: "rgba(255, 255, 255, 0.12)",
      darkItemSelectedColor: "#ffffff",
      darkGroupTitleColor: "rgba(255, 255, 255, 0.45)",
      itemBorderRadius: 6,
      itemHeight: 38,
      itemMarginInline: 10,
      groupTitleFontSize: 11,
      iconSize: 16,
    },
    Card: {
      headerFontSize: 15,
      headerHeight: 52,
      paddingLG: 20,
      boxShadowTertiary: "none",
    },
    Table: {
      headerBg: "#faf9f6",
      headerColor: brand.muted,
      headerSplitColor: "transparent",
      borderColor: brand.border,
      rowHoverBg: "#f7f6f1",
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 12,
    },
    Button: {
      fontWeight: 500,
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
    },
    Tabs: {
      itemColor: brand.muted,
      titleFontSize: 14,
    },
    Tag: {
      defaultBg: "#f3f2ed",
    },
    Breadcrumb: {
      itemColor: brand.muted,
      lastItemColor: brand.ink,
      fontSize: 13,
    },
    Drawer: {
      footerPaddingBlock: 12,
    },
    Modal: {
      titleFontSize: 16,
    },
  },
};
